import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (err) {
      console.warn("Auth refresh failed", err);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const setAuthedUser = useCallback((u) => {
    setUser(u);
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      console.warn("Logout request failed", err);
    }
    setUser(null);
    await refresh();
  }, [refresh]);

  const isGuest = !user || user?.guest;
  const isAuthed = !!user && !user.guest;

  // Stable context value — prevents every consumer from re-rendering when
  // an unrelated ancestor re-renders. `user`, `loading`, and callbacks are
  // the only cells that legitimately change.
  const value = useMemo(
    () => ({ user, loading, refresh, logout, isGuest, isAuthed, setAuthedUser }),
    [user, loading, refresh, logout, isGuest, isAuthed, setAuthedUser],
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
